import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
// ✅ FIX: Use a default import for the service object
import blogService from '../../services/blogService';
import type { IBlog } from '../../types/blog';
import Loader from '../../components/common/Loader';
import { showToast } from '../../utils/showToast';
import { User, Clock, Tags } from 'lucide-react';

const BlogDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<IBlog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Scroll to top when the component mounts
    window.scrollTo(0, 0);
    
    if (slug) {
      const fetchBlog = async () => {
        setIsLoading(true);
        try {
          // ✅ FIX: Call the correct function from the service object
          const data = await blogService.getBlogBySlug(slug);
          setBlog(data);
        } catch (err: any) {
          setError(err.message);
          showToast(err.message, { type: 'error' });
        } finally {
          setIsLoading(false);
        }
      };

      fetchBlog();
    }
  }, [slug]);

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen"><Loader /></div>;
  }

  if (error) {
    return <div className="text-center py-10"><p className="text-red-600">Error: {error}</p></div>;
  }

  if (!blog) {
    return <div className="text-center py-10"><p className="text-gray-500">Blog post not found.</p></div>;
  }

  return (
    <div className="bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <article className="bg-white rounded-lg shadow-lg p-6 md:p-10">
          {blog.image && (
            <img src={blog.image} alt={blog.title} className="w-full h-auto max-h-[500px] object-cover rounded-lg mb-6" />
          )}

          <div className="mb-4">
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Tags size={14} className="mr-2"/>
                {blog.tags.map(tag => (
                    <Link key={tag} to={`/blogs/tag/${tag}`} className="mr-2 uppercase font-semibold hover:text-blue-600">
                        {tag}
                    </Link>
                ))}
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">{blog.title}</h1>
          </div>

          <div className="flex flex-wrap items-center text-sm text-gray-500 mb-6 border-y py-3 gap-x-4 gap-y-2">
            <div className="flex items-center"><User size={14} className="mr-2" /><span>By {blog.authorName}</span></div>
            <div className="flex items-center"><Clock size={14} className="mr-2" /><span>{new Date(blog.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
            <div className="flex items-center"><Clock size={14} className="mr-2" /><span>{blog.readingTime} min read</span></div>
          </div>
          
          {/* Use Tailwind's typography plugin for beautiful article styling */}
          <div
            className="prose lg:prose-xl max-w-none"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* We can add the Like button and Comment Section here next */}
        </article>
      </div>
    </div>
  );
};

export default BlogDetails;